// apps/admin/src/components/SystemSettingsDialog.tsx
/**
 * System Settings Dialog
 * Allows super-admins to configure health check URLs
 */

import { useState, useEffect } from "react";
import { useQuery, useMutation } from "convex/react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Button,
  Input,
  Label,
  useToast,
} from "@jourfx/ui";
import { Settings, Loader2 } from "lucide-react";
import { api } from "@jourfx/convex/client";

interface SystemSettingsDialogProps {
  trigger?: React.ReactNode;
}

type SystemSetting = {
  key: string;
  value?: string | null;
};

export function SystemSettingsDialog({ trigger }: SystemSettingsDialogProps) {
  const { t } = useTranslation("system");
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    internalAppUrl: "",
    clientAppUrl: "",
    livekitUrl: "",
    minioUrl: "",
    smtpHost: "",
    smtpPort: "",
  });

  // Get current settings
  const settings = useQuery(api.admin.systemSettings.getByCategory, { category: "healthCheck" });
  const setMultiple = useMutation(api.admin.systemSettings.setMultiple);

  const buildFormData = (entries?: SystemSetting[] | null) => {
    const next = {
      internalAppUrl: "",
      clientAppUrl: "",
      livekitUrl: "",
      minioUrl: "",
      smtpHost: "",
      smtpPort: "",
    };

    if (!entries) return next;

    const keyMap: Record<string, keyof typeof next> = {
      "healthCheck.internalAppUrl": "internalAppUrl",
      "healthCheck.clientAppUrl": "clientAppUrl",
      "healthCheck.livekitUrl": "livekitUrl",
      "healthCheck.minioUrl": "minioUrl",
      "healthCheck.smtpHost": "smtpHost",
      "healthCheck.smtpPort": "smtpPort",
    };

    for (const setting of entries) {
      const formKey = keyMap[setting.key];
      if (formKey) next[formKey] = setting.value || "";
    }

    return next;
  };

  // Update form when settings load
  useEffect(() => {
    if (!settings) return;
    setFormData(buildFormData(settings as SystemSetting[]));
  }, [settings]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setFormData(buildFormData(settings as SystemSetting[] | undefined));
    }
    setOpen(nextOpen);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await setMultiple({
        settings: [
          { key: "healthCheck.internalAppUrl", value: formData.internalAppUrl },
          { key: "healthCheck.clientAppUrl", value: formData.clientAppUrl },
          { key: "healthCheck.livekitUrl", value: formData.livekitUrl },
          { key: "healthCheck.minioUrl", value: formData.minioUrl },
          { key: "healthCheck.smtpHost", value: formData.smtpHost },
          { key: "healthCheck.smtpPort", value: formData.smtpPort },
        ],
      });
      toast({
        title: t("settings.saved", "Settings saved"),
        description: t(
          "settings.savedDesc",
          "Health check URLs will be used on the next check cycle."
        ),
      });
      setOpen(false);
    } catch {
      toast({
        variant: "destructive",
        title: t("settings.saveError", "Error saving settings"),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (field: keyof typeof formData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm">
            <Settings className="h-4 w-4 mr-2" />
            {t("configureUrls", "Configure URLs")}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("settings.title", "Health Check Settings")}</DialogTitle>
          <DialogDescription>
            {t("settings.subtitle", "Configure URLs for service monitoring")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <p className="text-xs text-muted-foreground">
            {t("settings.envFallback", "Falls back to environment variable if empty")}
          </p>

          <div className="space-y-2">
            <Label htmlFor="internalAppUrl">
              {t("settings.internalAppUrl", "Internal App URL")}
            </Label>
            <Input
              id="internalAppUrl"
              placeholder="https://app.example.com"
              value={formData.internalAppUrl}
              onChange={handleChange("internalAppUrl")}
            />
            <p className="text-xs text-muted-foreground">
              {t("settings.internalAppUrlDesc", "URL for Internal App health check")}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="clientAppUrl">
              {t("settings.clientAppUrl", "Client App URL")}
            </Label>
            <Input
              id="clientAppUrl"
              placeholder="https://portal.example.com"
              value={formData.clientAppUrl}
              onChange={handleChange("clientAppUrl")}
            />
            <p className="text-xs text-muted-foreground">
              {t("settings.clientAppUrlDesc", "URL for Client App health check")}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="livekitUrl">
              {t("settings.livekitUrl", "LiveKit/OpenVidu URL")}
            </Label>
            <Input
              id="livekitUrl"
              placeholder="wss://video.example.com"
              value={formData.livekitUrl}
              onChange={handleChange("livekitUrl")}
            />
            <p className="text-xs text-muted-foreground">
              {t("settings.livekitUrlDesc", "WebSocket URL for video service")}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="minioUrl">
              {t("settings.minioUrl", "MinIO/S3 Endpoint URL")}
            </Label>
            <Input
              id="minioUrl"
              placeholder="https://s3.example.com"
              value={formData.minioUrl}
              onChange={handleChange("minioUrl")}
            />
            <p className="text-xs text-muted-foreground">
              {t("settings.minioUrlDesc", "HTTP URL for storage service")}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="smtpHost">
                {t("settings.smtpHost", "SMTP Host")}
              </Label>
              <Input
                id="smtpHost"
                placeholder="smtp.example.com"
                value={formData.smtpHost}
                onChange={handleChange("smtpHost")}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="smtpPort">
                {t("settings.smtpPort", "SMTP Port")}
              </Label>
              <Input
                id="smtpPort"
                placeholder="587"
                value={formData.smtpPort}
                onChange={handleChange("smtpPort")}
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            {t("actions.cancel", "Cancel")}
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {t("actions.save", "Save")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
